/**
 * @fileoverview TradingView Advanced Charts Adapter
 * 
 * Provides a clean interface to TradingView's Advanced Charts library
 * with CAP signal overlay support.
 * 
 * Prerequisites:
 * 1. Download TradingView Advanced Charts from their GitHub
 * 2. Place library files in public/charting_library/
 * 3. Load the library script before using this adapter
 * 
 * @see https://www.tradingview.com/HTML5-stock-forex-bitcoin-charting-library/
 * @module external/tradingview/advanced-charts
 */

import { createCAPDatafeed, createHybridDatafeed, CAP_EXCHANGE } from './cap-datafeed.js';
import { TV_THEME, SIGNAL_TO_SYMBOL, DEFAULT_SYMBOL } from './config.js';
import { SIGNAL_METADATA, CATEGORIES } from '../../api/capitulators.js';

/**
 * Check if TradingView Advanced Charts library is loaded
 * @returns {boolean}
 */
export function isAdvancedChartsLoaded() {
  return typeof window !== 'undefined' && 
         typeof window.TradingView !== 'undefined' &&
         typeof window.TradingView.widget !== 'undefined';
}

/**
 * Loads the TradingView Advanced Charts library
 * @returns {Promise<void>}
 */
export async function loadAdvancedCharts() {
  if (isAdvancedChartsLoaded()) {
    console.log('[AdvancedCharts] Already loaded');
    return;
  }
  
  return new Promise((resolve, reject) => {
    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="charting_library"]');
    if (existingScript) {
      existingScript.addEventListener('load', resolve);
      existingScript.addEventListener('error', reject);
      return;
    }
    
    // Load the library
    const script = document.createElement('script');
    script.src = '/charting_library/charting_library.js';
    script.async = true;
    
    script.onload = () => {
      console.log('[AdvancedCharts] Library loaded successfully');
      resolve();
    };
    
    script.onerror = () => {
      console.error('[AdvancedCharts] Failed to load library');
      reject(new Error('Failed to load TradingView Advanced Charts library'));
    };
    
    document.head.appendChild(script);
  });
}

/**
 * Default chart configuration matching Gothic Terminal theme
 */
const DEFAULT_CHART_CONFIG = {
  // Library path (where charting_library folder is served)
  library_path: '/charting_library/',
  
  // Appearance
  theme: 'Dark',
  loading_screen: {
    backgroundColor: '#0e1317',
    foregroundColor: '#97B8A0',
  },
  
  // Overrides for Gothic Terminal theme
  overrides: {
    // Background
    'paneProperties.background': '#0e1317',
    'paneProperties.backgroundType': 'solid',
    
    // Grid
    'paneProperties.vertGridProperties.color': 'rgba(151, 184, 160, 0.1)',
    'paneProperties.horzGridProperties.color': 'rgba(151, 184, 160, 0.1)',
    
    // Candle colors
    'mainSeriesProperties.candleStyle.upColor': '#97B8A0',
    'mainSeriesProperties.candleStyle.downColor': '#B2332A',
    'mainSeriesProperties.candleStyle.borderUpColor': '#97B8A0',
    'mainSeriesProperties.candleStyle.borderDownColor': '#B2332A',
    'mainSeriesProperties.candleStyle.wickUpColor': '#97B8A0',
    'mainSeriesProperties.candleStyle.wickDownColor': '#B2332A',
    
    // Line chart color (for signals)
    'mainSeriesProperties.lineStyle.color': '#00EEE5',
    'mainSeriesProperties.lineStyle.linewidth': 2,
    
    // Area chart
    'mainSeriesProperties.areaStyle.color1': 'rgba(0, 238, 229, 0.3)',
    'mainSeriesProperties.areaStyle.color2': 'rgba(0, 238, 229, 0.05)',
    'mainSeriesProperties.areaStyle.linecolor': '#00EEE5',
    
    // Scales
    'scalesProperties.textColor': '#97B8A0',
    'scalesProperties.lineColor': 'rgba(151, 184, 160, 0.2)',
    
    // Crosshair
    'paneProperties.crossHairProperties.color': '#D4A85F',
  },
  
  // Studies (indicators) overrides
  studies_overrides: {
    'volume.volume.color.0': '#B2332A',
    'volume.volume.color.1': '#97B8A0',
  },
  
  // Features
  disabled_features: [
    'header_symbol_search',      // We provide our own
    'header_compare',            // We handle compare ourselves
    'symbol_search_hot_key',
    'display_market_status',
    'popup_hints',
  ],
  enabled_features: [
    'study_templates',
    'side_toolbar_in_fullscreen_mode',
    'header_in_fullscreen_mode',
    'hide_left_toolbar_by_default',
  ],
  
  // Locale
  locale: 'en',
  
  // Auto-sizing
  autosize: true,
  fullscreen: false,
  
  // Time
  timezone: 'America/New_York',
  
  // Chart type default (line for signals)
  charts_storage_url: '',
  client_id: 'capitulators.io',
  user_id: 'public',
};

/**
 * Creates an Advanced Charts widget with CAP datafeed
 * 
 * @param {HTMLElement} container - Container element for the chart
 * @param {Object} options - Chart options
 * @param {string} options.symbol - Initial symbol (e.g., 'CAP:NET_LIQUIDITY')
 * @param {string} [options.interval='1D'] - Initial interval
 * @param {number} [options.height=500] - Chart height
 * @param {boolean} [options.showToolbar=true] - Show top toolbar
 * @param {string} [options.chartType='2'] - Chart type (1=bars, 2=candles, 3=line, 4=area)
 * @returns {Promise<Object>} Chart instance with control methods
 */
export async function createAdvancedChart(container, options = {}) {
  const {
    symbol = `${CAP_EXCHANGE}:NET_LIQUIDITY`,
    interval = '1D',
    height = 500,
    showToolbar = true,
    chartType = '3', // Default to line for signals
  } = options;
  
  // Ensure library is loaded
  if (!isAdvancedChartsLoaded()) {
    await loadAdvancedCharts();
  }
  
  // Generate unique container ID
  const containerId = container.id || `tv-chart-${Date.now()}`;
  container.id = containerId;
  container.style.height = `${height}px`;
  
  // Create datafeed
  const datafeed = createCAPDatafeed();
  
  // Merge configuration
  const config = {
    ...DEFAULT_CHART_CONFIG,
    container: containerId,
    datafeed: datafeed,
    symbol: symbol,
    interval: interval,
    height: height,
    toolbar_bg: TV_THEME.toolbar_bg,
    hide_top_toolbar: !showToolbar,
  };
  
  // Create widget
  const widget = new window.TradingView.widget(config);
  
  // Wait for chart to be ready
  await new Promise((resolve, reject) => {
    widget.onChartReady(() => {
      console.log('[AdvancedCharts] Chart ready');
      resolve();
    });
    
    // Timeout after 10 seconds
    setTimeout(() => reject(new Error('Chart initialization timeout')), 10000);
  });
  
  // Get chart instance for advanced operations
  const chart = widget.chart();
  
  // Set chart type to line for signals
  if (symbol.startsWith(CAP_EXCHANGE)) {
    chart.setChartType(parseInt(chartType));
  }
  
  return {
    widget,
    chart,
    containerId,
    
    /**
     * Changes the main symbol
     * @param {string} newSymbol 
     */
    setSymbol(newSymbol) {
      const fullSymbol = newSymbol.includes(':') ? newSymbol : `${CAP_EXCHANGE}:${newSymbol}`;
      widget.setSymbol(fullSymbol, interval, () => {
        // Set to line chart for CAP signals
        if (fullSymbol.startsWith(CAP_EXCHANGE)) {
          chart.setChartType(parseInt(chartType));
        }
      });
    },
    
    /**
     * Changes the time interval
     * @param {string} newInterval 
     */
    setInterval(newInterval) {
      chart.setResolution(newInterval);
    },
    
    /**
     * Adds a compare symbol (overlay)
     * @param {string} compareSymbol - Symbol to overlay
     * @param {Object} [options] - Display options
     * @returns {Promise<Object>} Study info
     */
    async addOverlay(compareSymbol, options = {}) {
      const {
        lineColor = '#D4A85F',  // Lantern gold
        lineWidth = 2,
        lineStyle = 0,  // 0=solid, 1=dotted, 2=dashed
      } = options;
      
      return new Promise((resolve) => {
        chart.createStudy(
          'Compare',
          false,
          false,
          {
            source: 'close',
            symbol: compareSymbol,
          },
          {
            'plot.color': lineColor,
            'plot.linewidth': lineWidth,
            'plot.linestyle': lineStyle,
          },
          (studyId) => {
            console.log('[AdvancedCharts] Added overlay:', compareSymbol, studyId);
            resolve({ studyId, symbol: compareSymbol });
          }
        );
      });
    },
    
    /**
     * Removes an overlay study
     * @param {Object} overlayInfo - Info from addOverlay
     */
    removeOverlay(overlayInfo) {
      if (overlayInfo?.studyId) {
        chart.removeEntity(overlayInfo.studyId);
      }
    },
    
    /**
     * Adds a CAP signal as overlay on existing chart
     * @param {string} capSignalId - CAP signal ID (e.g., 'NET_LIQUIDITY')
     * @param {Object} [options] - Display options
     */
    async addCAPSignalOverlay(capSignalId, options = {}) {
      const fullSymbol = `${CAP_EXCHANGE}:${capSignalId}`;
      return this.addOverlay(fullSymbol, {
        lineColor: '#00EEE5',  // Liquidity cyan
        lineWidth: 2,
        ...options,
      });
    },
    
    /**
     * Takes a screenshot of the chart
     * @returns {Promise<string>} Base64 image data
     */
    async takeScreenshot() {
      return new Promise((resolve) => {
        widget.takeScreenshot().then((data) => {
          resolve(data);
        });
      });
    },
    
    /**
     * Gets current visible time range
     * @returns {{from: number, to: number}}
     */
    getVisibleRange() {
      return chart.getVisibleRange();
    },
    
    /**
     * Sets visible time range
     * @param {number} from - Unix timestamp
     * @param {number} to - Unix timestamp
     */
    setVisibleRange(from, to) {
      chart.setVisibleRange({ from, to });
    },
    
    /**
     * Destroys the chart and cleans up
     */
    destroy() {
      try {
        widget.remove();
        container.innerHTML = '';
        console.log('[AdvancedCharts] Destroyed');
      } catch (e) {
        console.warn('[AdvancedCharts] Destroy error:', e);
      }
    },
  };
}

/**
 * Creates a dual-pane chart with market data and CAP signal
 * 
 * @param {HTMLElement} container - Container element
 * @param {Object} options
 * @param {string} options.marketSymbol - Market symbol (e.g., 'TVC:VIX')
 * @param {string} options.capSignal - CAP signal ID (e.g., 'NET_LIQUIDITY')
 * @param {string} [options.interval='1D'] - Time interval
 * @param {number} [options.height=600] - Total height
 * @returns {Promise<Object>} Chart instance
 */
export async function createDualPaneChart(container, options) {
  const {
    marketSymbol,
    capSignal,
    interval = '1D',
    height = 600,
  } = options;
  
  // Create the main chart with market data
  const chartInstance = await createAdvancedChart(container, {
    symbol: marketSymbol,
    interval,
    height,
    chartType: '2', // Candles for market data
  });
  
  // Add CAP signal as a separate pane
  // Note: This requires the "study" feature and the library to support it
  // The actual implementation may vary based on library version
  
  // For now, add as overlay on same pane
  const overlay = await chartInstance.addCAPSignalOverlay(capSignal);
  
  return {
    ...chartInstance,
    overlay,
    
    /**
     * Swaps primary and overlay symbols
     */
    swapSymbols() {
      const currentSymbol = chartInstance.widget.activeChart().symbol();
      chartInstance.setSymbol(`${CAP_EXCHANGE}:${capSignal}`);
      chartInstance.removeOverlay(overlay);
      chartInstance.addOverlay(marketSymbol);
    },
  };
}

export default createAdvancedChart;
